import React, { useState } from 'react';
import { 
  FaChevronDown, 
  FaQuestionCircle, 
  FaLightbulb, 
  FaUsers, 
  FaPhone, 
  FaEnvelope,
  FaSearch,
  FaCog,
  FaShieldAlt,
  FaRocket,
  FaCloud,
  FaChartLine,
  FaHeart,
  FaCheckCircle
} from 'react-icons/fa';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

const FAQItem = ({ question, answer, isOpen, toggleOpen, index, category }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const getCategoryIcon = (cat) => {
    switch (cat?.toLowerCase()) {
      case 'general': return FaQuestionCircle;
      case 'technical': return FaCog;
      case 'security': return FaShieldAlt;
      case 'pricing': return FaChartLine;
      case 'support': return FaUsers;
      default: return FaLightbulb;
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat?.toLowerCase()) {
      case 'general': return 'from-blue-500 to-cyan-500';
      case 'technical': return 'from-purple-500 to-pink-500';
      case 'security': return 'from-green-500 to-teal-500';
      case 'pricing': return 'from-orange-500 to-red-500';
      case 'support': return 'from-indigo-500 to-blue-500';
      default: return 'from-blue-500 to-purple-500';
    }
  };

  const IconComponent = getCategoryIcon(category);

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative mb-6"
    >
      {/* Background Glow Effect */}
      <div className={`absolute inset-0 bg-gradient-to-r ${getCategoryColor(category)} opacity-0 group-hover:opacity-10 transition-all duration-700 rounded-3xl blur-xl`}></div>
      
      <div className={`relative bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden ${isOpen ? 'ring-2 ring-blue-500/20' : ''}`}>
        <button 
          className="flex w-full justify-between items-center text-left p-8 focus:outline-none group/btn"
          onClick={toggleOpen}
          aria-expanded={isOpen}
        >
          <div className="flex items-start space-x-4 flex-1 pr-4">
            {/* Category Icon */}
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${getCategoryColor(category)} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
              <IconComponent className="text-white text-lg" />
            </div>
            
            {/* Question */}
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 group-hover/btn:text-transparent group-hover/btn:bg-clip-text group-hover/btn:bg-gradient-to-r group-hover/btn:from-blue-600 group-hover/btn:to-purple-600 transition-all duration-300 leading-tight">
                {question}
              </h3>
              {category && (
                <span className={`inline-block mt-2 px-3 py-1 text-xs font-semibold text-white bg-gradient-to-r ${getCategoryColor(category)} rounded-full`}>
                  {category}
                </span>
              )}
            </div>
          </div>
          
          {/* Chevron with Enhanced Animation */}
          <motion.div 
            animate={{ 
              rotate: isOpen ? 180 : 0,
              scale: isOpen ? 1.1 : 1
            }}
            transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
            className={`w-10 h-10 rounded-xl bg-gradient-to-r ${getCategoryColor(category)} flex items-center justify-center shadow-lg flex-shrink-0`}
          >
            <FaChevronDown className="text-white" />
          </motion.div>
        </button>
        
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="px-8 pb-8">
                <div className="pl-16">
                  <div className={`w-full h-px bg-gradient-to-r ${getCategoryColor(category)} mb-6 opacity-20`}></div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="prose prose-lg max-w-none"
                  >
                    <p className="text-gray-700 leading-relaxed text-lg">{answer}</p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Decorative Elements */}
        <div className={`absolute -bottom-2 -right-2 w-20 h-20 bg-gradient-to-r ${getCategoryColor(category)} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity duration-700`}></div>
        <div className={`absolute -top-2 -left-2 w-12 h-12 bg-gradient-to-r ${getCategoryColor(category)} opacity-5 rounded-full blur-xl group-hover:opacity-10 transition-opacity duration-500`}></div>
      </div>
    </motion.div>
  );
};

const SearchBar = ({ searchTerm, setSearchTerm }) => {
  return (
    <motion.div 
      className="relative max-w-2xl mx-auto mb-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
          <FaSearch className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search frequently asked questions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-14 pr-6 py-6 text-lg border border-gray-200/50 rounded-2xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 shadow-lg hover:shadow-xl"
        />
      </div>
      {searchTerm && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute top-full mt-2 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium"
        >
          Searching: "{searchTerm}"
        </motion.div>
      )}
    </motion.div>
  );
};

const StatsSection = () => {
  const stats = [
    { number: "500+", label: "Questions Answered", icon: FaQuestionCircle },
    { number: "24/7", label: "Support Available", icon: FaUsers },
    { number: "< 2min", label: "Average Response Time", icon: FaRocket },
    { number: "99.9%", label: "Satisfaction Rate", icon: FaHeart }
  ];

  return (
    <motion.div 
      className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.5 }}
    >
      {stats.map((stat, index) => (
        <motion.div 
          key={index}
          className="text-center group"
          whileHover={{ scale: 1.05, y: -5 }}
          transition={{ duration: 0.2 }}
        >
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
            <stat.icon className="text-white text-2xl" />
          </div>
          <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.number}</div>
          <div className="text-blue-200 font-medium">{stat.label}</div>
        </motion.div>
      ))}
    </motion.div>
  );
};

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // SEO data for FAQ page
  const seoData = {
    title: "FAQ - Frequently Asked Questions",
    description: `Get answers to frequently asked questions about ${contentData.company.name}'s cloud services, AWS migration, DevOps, and support. Find quick solutions to common queries about our cloud consulting services.`,
    keywords: "FAQ, Cloud Services Questions, AWS Migration FAQ, DevOps Support, Cloud Consulting Help, Technical Support"
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Enhanced FAQ filtering and categorization
  const processedFAQs = contentData.faqs ? contentData.faqs.map((faq, index) => ({
    ...faq,
    category: faq.category || ['general', 'technical', 'security', 'pricing', 'support'][index % 5]
  })) : [];

  const filteredFAQs = processedFAQs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <SEO {...seoData} />
      
      {/* Enhanced Hero Section */}
      <section className="relative py-32 md:py-40 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.7, 0.3]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
          <motion.div 
            className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.4, 0.8, 0.4]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2
            }}
          />
          <motion.div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-full blur-3xl"
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <div className="container relative z-10">
          <motion.div 
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Enhanced Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-8 py-4 mb-8"
            >
              <FaQuestionCircle className="text-blue-400 mr-3 text-xl" />
              <span className="text-white font-semibold text-lg">Help Center</span>
            </motion.div>

            <h1 className="mb-8 text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              <span className="block mb-4">Frequently Asked</span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Questions
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-12">
              Get instant answers to common questions about our cloud services, solutions, and support. 
              Can't find what you're looking for? Our expert team is here to help.
            </p>

            {/* Enhanced Stats */}
            <StatsSection />
          </motion.div>
        </div>
      </section>

      {/* Enhanced FAQ Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {/* Search Bar */}
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

            {/* FAQs */}
            {filteredFAQs && filteredFAQs.length > 0 ? (
              <div className="space-y-0">
                {filteredFAQs.map((faq, index) => (
                  <FAQItem 
                    key={index}
                    question={faq.question}
                    answer={faq.answer}
                    category={faq.category}
                    isOpen={openIndex === index}
                    toggleOpen={() => toggleFAQ(index)}
                    index={index}
                  />
                ))}
              </div>
            ) : searchTerm ? (
              <motion.div 
                className="bg-white/80 backdrop-blur-sm rounded-3xl border border-gray-200/50 p-16 text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-20 h-20 bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FaSearch className="text-3xl text-orange-500" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">No results found</h3>
                <p className="text-gray-500">Try different keywords or browse our categories below</p>
              </motion.div>
            ) : (
              <motion.div 
                className="bg-white/80 backdrop-blur-sm rounded-3xl border border-gray-200/50 p-16 text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FaQuestionCircle className="text-3xl text-blue-500" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">No FAQs available</h3>
                <p className="text-gray-500">Please check back soon for frequently asked questions</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Enhanced Contact Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background with Advanced Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900"></div>
          <motion.div 
            className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.3),transparent_50%)]"
            animate={{
              background: [
                'radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.3),transparent_50%)',
                'radial-gradient(circle_at_70%_30%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_30%_70%,rgba(139,92,246,0.3),transparent_50%)',
                'radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.3),transparent_50%)'
              ]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(-45deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        </div>
        
        <div className="container relative z-10">
          <motion.div 
            className="max-w-5xl mx-auto text-center text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-8">
              <FaLightbulb className="text-blue-400 mr-2" />
              <span className="text-white font-semibold">Still Need Help?</span>
            </div>

            <h2 className="mb-8 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="block mb-4">Can't Find What</span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                You're Looking For?
              </span>
            </h2>
            
            <p className="mb-12 text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Our expert support team is standing by to help you with any questions about our cloud services and solutions.
            </p>
            
            {/* Enhanced Contact Options */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  title: "Contact Support",
                  description: "Get help from our expert team",
                  icon: FaUsers,
                  action: "Contact Us",
                  link: "/contact",
                  gradient: "from-blue-600 to-cyan-600"
                },
                {
                  title: "Call Direct",
                  description: "Speak with us right now",
                  icon: FaPhone,
                  iconClassName: "rotate-90",
                  action: contentData.company?.contact?.phone || "+1 (555) 123-4567",
                  link: `tel:${contentData.company?.contact?.phone || "+15551234567"}`,
                  gradient: "from-green-600 to-teal-600"
                },
                {
                  title: "Email Us",
                  description: "Send us your questions",
                  icon: FaEnvelope,
                  action: "Send Email",
                  link: `mailto:${contentData.company?.contact?.email || "support@dhyontechnologies.com"}`,
                  gradient: "from-purple-600 to-pink-600"
                }
              ].map((option, index) => (
                <motion.div
                  key={index}
                  className="group"
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <a 
                    href={option.link}
                    className={`block p-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl hover:bg-white/20 transition-all duration-300`}
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${option.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <option.icon className={`text-white text-2xl ${option.iconClassName || ''}`} />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{option.title}</h3>
                    <p className="text-blue-200 mb-4">{option.description}</p>
                    <div className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${option.gradient} rounded-xl font-semibold transition-all duration-300 group-hover:scale-105`}>
                      {option.action}
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>

            {/* Success Rate Badge */}
            <motion.div 
              className="inline-flex items-center bg-green-500/20 border border-green-400/30 rounded-full px-6 py-3"
              whileHover={{ scale: 1.05 }}
            >
              <FaCheckCircle className="text-green-400 mr-2" />
              <span className="text-white font-semibold">99.9% Customer Satisfaction Rate</span>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQPage; 